import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Card,
} from "react-bootstrap";
import "./styles/registrationForm.css";
import {
  registerNewUser,
  getCountriesWithStates,
  getFieldStaffNames
} from "./registrationFormApi";
import ReusableModal from "../../components/atoms/modal/ReusableModal";

const RegistrationForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    companyName: "",
    location: "",
    address: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    phone: "",
    fax: "",
    firstName: "",
    lastName: "",
    email: "",
    rep: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [countriesData, setCountriesData] = useState([]);
  const [statesData, setStatesData] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaError, setRecaptchaError] = useState("");
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const isUSCountry = () => {
    return formData.country === "United States" ||
      formData.country === "US" ||
      formData.country === "USA" ||
      formData.country === "United States of America";
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load Google reCAPTCHA script and render widget for registration
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY";
    if (!siteKey) return;

    const renderRecaptcha = () => {
      try {
        if (!window.grecaptcha) return;
        if (recaptchaWidgetId !== null) return;

        const doRender = () => {
          try {
            // ensure container still present
            const container = document.getElementById("registration-recaptcha-container");
            if (!container) return;
            const id = window.grecaptcha.render("registration-recaptcha-container", {
              sitekey: siteKey,
              callback: (token) => {
                setRecaptchaToken(token);
                setRecaptchaError("");
              },
              "expired-callback": () => {
                setRecaptchaToken("");
                setRecaptchaError("reCAPTCHA expired. Please complete it again.");
              },
            });
            setRecaptchaWidgetId(id);
          } catch (err) {
            console.warn("reCAPTCHA internal render failed", err);
          }
        };

        // Prefer grecaptcha.ready when available — it ensures the API is initialized
        if (typeof window.grecaptcha.ready === "function") {
          window.grecaptcha.ready(doRender);
        } else {
          // fallback: try to render after a short delay
          setTimeout(doRender, 300);
        }
      } catch (e) {
        console.warn("reCAPTCHA render failed", e);
      }
    };

    if (window.grecaptcha && window.grecaptcha.render) {
      renderRecaptcha();
      return;
    }

    const scriptId = "recaptcha-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // prefer grecaptcha.ready, otherwise fallback after a short delay
        if (window.grecaptcha && typeof window.grecaptcha.ready === "function") {
          window.grecaptcha.ready(renderRecaptcha);
        } else {
          setTimeout(renderRecaptcha, 300);
        }
      };
      document.body.appendChild(script);
    } else {
      const iv = setInterval(() => {
        if (window.grecaptcha && (typeof window.grecaptcha.ready === "function" || window.grecaptcha.render)) {
          clearInterval(iv);
          // if ready exists prefer it
          if (typeof window.grecaptcha.ready === "function") {
            window.grecaptcha.ready(renderRecaptcha);
          } else {
            renderRecaptcha();
          }
        }
      }, 150);
    }
  }, [recaptchaWidgetId]);

  const fetchInitialData = async () => {
    try {

      const countriesResponse = await getCountriesWithStates();

      if (countriesResponse.success && Array.isArray(countriesResponse.data)) {
        setCountriesData(countriesResponse.data);
      } else {
        console.error("Invalid countries data:", countriesResponse);
      }

      try {
        const repsRes = await getFieldStaffNames();
        const repsArray = Array.isArray(repsRes)
          ? repsRes
          : Array.isArray(repsRes?.data)
          ? repsRes.data
          : [];

        const formatted = repsArray
          .filter((r) => r != null) // removes null & undefined
          .map((r) => ({
            fullName: r?.fullName ?? r?.name ?? String(r),
            userID: r?.userID ?? r?.userId ?? r?.id ?? 0,
          }));
        setSalesReps(formatted);
      } catch (err) {
        console.error("Failed to fetch sales reps:", err);

        setSalesReps([]);
      }

    } catch (error) {
      console.error("Error fetching initial data:", error);
      setModalMessage("Failed to load initial data. Please refresh the page.");
      setShowErrorModal(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "country") {
      const selectedCountry = countriesData.find(countryObj => {
        const countryName = countryObj.country || countryObj.name || countryObj.countryName;
        return countryName === value;
      });

      if (selectedCountry) {
        const states = selectedCountry.states || selectedCountry.stateProvinces || [];
        setStatesData(states);
        setFormData(prev => ({ ...prev, country: value, state: "" }));
        if (errors.state) {
          setErrors(prev => ({ ...prev, state: "" }));
        }
      } else {
        setStatesData([]);
        setFormData(prev => ({ ...prev, country: value, state: "" }));
      }

      if (errors.zipCode) {
        setErrors(prev => ({ ...prev, zipCode: "" }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));

    if (isSubmitted || touched[name]) {
      validateSingleField(name);
    }
  };

  const validateSingleField = (fieldName) => {
    const value = formData[fieldName];
    let error = "";

    if (fieldName !== 'fax' && !(fieldName === 'zipCode' && !isUSCountry()) && !value?.trim()) {
      error = `Please enter ${fieldName.replace(/([A-Z])/g, " $1").toLowerCase()}`;
    }

    if (fieldName === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      error = "Please enter a valid email address";
    }

    if (fieldName === 'zipCode' && value && !/^\d+$/.test(value)) {
      error = "Zip code should contain only numbers";
    }

    if (fieldName === 'phone' && value && !/^[\d\-\+\(\)\s]+$/.test(value)) {
      error = "Please enter a valid phone number";
    }

    setErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  const validateForm = () => {
    const newErrors = {};

    const baseRequiredFields = [
      'companyName', 'location', 'address', 'country', 'state',
      'city', 'phone', 'firstName', 'lastName', 'email', 'rep'
    ];

    const requiredFields = isUSCountry()
      ? [...baseRequiredFields, 'zipCode']
      : baseRequiredFields;

    requiredFields.forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = `Please enter ${field.replace(/([A-Z])/g, " $1").toLowerCase()}`;
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.zipCode && !/^\d+$/.test(formData.zipCode)) {
      newErrors.zipCode = "Zip code should contain only numbers";
    }

    if (formData.phone && !/^[\d\-\+\(\)\s]+$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    try {
      // Require reCAPTCHA before submitting
      if (!recaptchaToken) {
        setRecaptchaError("Please verify you are not a robot.");
        setIsLoading(false);
        return;
      }

      const payload = { ...formData, recaptchaToken };
      const response = await registerNewUser(payload);

      if (response.success) {
        setModalMessage("Registration was completed successfully, and a confirmation email has been sent to you. The Feed Assay administrator team will review your registration.");
        setShowSuccessModal(true);
      } else {
        setModalMessage(response.error || "Registration failed. Please try again.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Something went wrong:", error);
      setModalMessage("Something went wrong. Please try again later.");
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
      // reset recaptcha after attempt
      try {
        if (window.grecaptcha && typeof window.grecaptcha.reset === "function" && recaptchaWidgetId !== null) {
          window.grecaptcha.reset(recaptchaWidgetId);
          setRecaptchaToken("");
        }
      } catch (e) {
        // ignore
      }
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate("/customer-login");
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/customer-login");
  };

  const shouldShowError = (fieldName) => {
    return errors[fieldName] && (isSubmitted || touched[fieldName]);
  };

  const renderField = (
    label,
    name,
    type = "text",
    placeholder = "",
    isSelect = false,
    options = [],
    required = true,
    disabled = false
  ) => (
    <Form.Group className="mb-3" controlId={name}>
      <Form.Label>
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <div className="form-group-with-icon position-relative">
        {isSelect ? (
          <Form.Select
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            className={shouldShowError(name) ? "is-invalid" : ""}
          >
            <option value="">Select {label}</option>
            {options.map((opt, i) => {
              let optionValue, optionLabel;

              if (typeof opt === 'string') {
                optionValue = optionLabel = opt;
              } else if (typeof opt === 'object') {
                if (name === 'country') {
                  optionValue = optionLabel = opt.country || opt.name || opt.countryName || String(opt);
                }
                else if (name === 'state') {
                  optionValue = optionLabel = opt.state || opt.name || opt.stateName || String(opt);
                }
                else if (name === 'rep') {
                  optionValue = optionLabel = opt.fullName || opt.name || String(opt);
                }
                else {
                  optionValue = optionLabel = opt.name || opt.value || String(opt);
                }
              } else {
                optionValue = optionLabel = String(opt);
              }

              return (
                <option key={i} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </Form.Select>
        ) : (
          <Form.Control
            type={type}
            name={name}
            placeholder={placeholder || `Enter ${label}`}
            value={formData[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            className={shouldShowError(name) ? "is-invalid" : ""}
          />
        )}
      </div>
      {shouldShowError(name) && <div className="invalid-feedback">{errors[name]}</div>}
    </Form.Group>
  );

  return (
    <>
      <Container className="py-4 registration-container">
        <Card className="shadow-sm">
          <Card.Body className="p-4">
            <h2>Register</h2>
            <Form className="registration-form" onSubmit={handleSubmit}>
              <Row>
                <Col lg={4} md={4} sm={12}>
                  {renderField("Company Name", "companyName")}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {renderField("Location", "location")}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {renderField("Address", "address")}
                </Col>
              </Row>

              <Row>
                <Col lg={4} md={4} sm={12}>
                  {renderField("Country", "country", "select", "", true, countriesData)}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {isUSCountry() ? (
                    renderField("State", "state", "select", "", true, statesData, true, !formData.country)
                  ) : (
                    <Form.Group className="mb-3" controlId="state">
                      <Form.Label>
                        State <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        placeholder="Enter State"
                        value={formData.state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!formData.country}
                        className={shouldShowError("state") ? "is-invalid" : ""}
                      />
                      {shouldShowError("state") && <div className="invalid-feedback">{errors.state}</div>}
                    </Form.Group>
                  )}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {renderField("City", "city")}
                </Col>
              </Row>

              <Row>
                <Col lg={4} md={4} sm={12}>
                  <Form.Group className="mb-3" controlId="zipCode">
                    <Form.Label>
                      Zip Code {isUSCountry() && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="zipCode"
                      placeholder="Enter Zip Code"
                      value={formData.zipCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={shouldShowError("zipCode") ? "is-invalid" : ""}
                    />
                    {shouldShowError("zipCode") && <div className="invalid-feedback">{errors.zipCode}</div>}
                  </Form.Group>
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {renderField("Phone", "phone")}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {renderField("Fax", "fax", "text", "", false, [], false)}
                </Col>
              </Row>

              <Row>
                <Col lg={4} md={4} sm={12}>
                  {renderField("First Name", "firstName")}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {renderField("Last Name", "lastName")}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {renderField("Email", "email", "email")}
                </Col>
              </Row>

              <Row>
                <Col lg={4} md={6} sm={12}>
                  <Form.Group className="mb-3" controlId="rep">
                    <Form.Label>
                      Select Rep <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Select
                        name="rep"
                        value={formData.rep}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`select-rep-container ${shouldShowError("rep") ? "is-invalid" : ""}`}
                      >
                        <option value="">Select Rep</option>
                        {salesReps.map((rep, index) => (
                          <option key={rep.userID || index} value={rep.fullName || rep.name}>
                            {rep.fullName || rep.name}
                          </option>
                        ))}
                      </Form.Select>
                      <div
                        className="info-icon"
                        title="Select a sales representative"
                      >
                        i
                      </div>
                    </div>
                    {shouldShowError("rep") && <div className="invalid-feedback d-block">{errors.rep}</div>}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <div id="registration-recaptcha-container" />
                {recaptchaError && <div className="text-danger mt-2">{recaptchaError}</div>}
              </Form.Group>

              <div className="d-flex justify-content-end mt-4 button-group">
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className="btn-common outlined-active-btn btn-md default-font-styles"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-common active-btn btn-md default-font-styles"
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>

      <ReusableModal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        title="Registration Successful"
        size="md"
        buttons={[
          {
            text: "OK",
            variant: "active",
            onClick: handleSuccessModalClose
          }
        ]}
      >
        <p>
          <strong>Registration was completed successfully.</strong>
          {' '}
          A confirmation email has been sent to you. The Feed Assay administrator team will review your registration.
        </p>
      </ReusableModal>

      <ReusableModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title="Registration Failed"
        size="md"
        buttons={[
          {
            text: "OK",
            variant: "active",
            onClick: () => setShowErrorModal(false)
          }
        ]}
      >
        <p>{modalMessage}</p>
      </ReusableModal>

      <ReusableModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        title="Cancel Registration"
        size="md"
        buttons={[
          {
            text: "No, Continue",
            variant: "outlined",
            onClick: () => setShowCancelModal(false)
          },
          {
            text: "Yes, Cancel",
            variant: "active",
            onClick: handleConfirmCancel
          }
        ]}
      >
        <p>Are you sure you want to cancel the registration? All entered data will be lost.</p>
      </ReusableModal>
    </>
  );
};

export default RegistrationForm;
